// Turns the computed performanceAnalytics object (ClientManagement.jsx) into a
// short, plain-English summary a coach can edit and share directly with a client.
// Pure function — no DB/network calls — so it can run instantly as analytics load.

// A client who joined 4 weeks ago shouldn't have their adherence averaged against
// a 12-week window just because that's the selected chart range — cap the window
// used for the briefing text at how long they've actually been a client.
export function getEffectiveBriefingWeeks(clientCreatedAt, selectedWeeks) {
  if (!clientCreatedAt) return selectedWeeks === 'all' ? 52 : selectedWeeks;
  const weeksSinceJoined = Math.max(
    1,
    Math.ceil((Date.now() - new Date(clientCreatedAt).getTime()) / (7 * 24 * 60 * 60 * 1000))
  );
  if (selectedWeeks === 'all') return weeksSinceJoined;
  return Math.min(selectedWeeks, weeksSinceJoined);
}

function describeAdherence(totalSessions, timeRangeWeeks, weeklyTarget) {
  const avgPerWeek = timeRangeWeeks > 0 ? totalSessions / timeRangeWeeks : 0;
  const weekWord = timeRangeWeeks === 1 ? 'week' : 'weeks';

  if (totalSessions === 0) {
    return `No logged workouts in the last ${timeRangeWeeks} ${weekWord} — let's find out what's getting in the way and get you back in.`;
  }

  // Compare against their actual assigned program's weekly day count when we have it,
  // rather than a generic guessed target — a 2-day/week program hitting 2/week is 100%,
  // not "under target".
  if (weeklyTarget && weeklyTarget > 0) {
    const targetLabel = `${Number.isInteger(weeklyTarget) ? weeklyTarget : weeklyTarget.toFixed(1)} planned session${weeklyTarget === 1 ? '' : 's'} a week`;
    const pct = avgPerWeek / weeklyTarget;
    if (pct >= 0.9) {
      return `Great consistency — you've trained ${totalSessions} time${totalSessions === 1 ? '' : 's'} over the last ${timeRangeWeeks} ${weekWord}, right on track with your ${targetLabel}.`;
    }
    if (pct >= 0.6) {
      return `You've trained ${totalSessions} time${totalSessions === 1 ? '' : 's'} over the last ${timeRangeWeeks} ${weekWord} — a little under your ${targetLabel}, but solid progress.`;
    }
    return `Only ${totalSessions} session${totalSessions === 1 ? '' : 's'} logged over the last ${timeRangeWeeks} ${weekWord}, against a ${targetLabel} — worth talking about what's making it hard to keep up.`;
  }

  // Fallback when there's no active program to read a target from
  if (avgPerWeek >= 3) {
    return `Great consistency — you've trained ${totalSessions} times over the last ${timeRangeWeeks} ${weekWord}, averaging ${avgPerWeek.toFixed(1)} sessions a week.`;
  }
  if (avgPerWeek >= 1.5) {
    return `You've trained ${totalSessions} times over the last ${timeRangeWeeks} ${weekWord} (about ${avgPerWeek.toFixed(1)} a week) — solid, though a bit more consistency would speed things up.`;
  }
  return `Only ${totalSessions} session${totalSessions === 1 ? '' : 's'} logged over the last ${timeRangeWeeks} ${weekWord} — worth talking about what's making it hard to get in more often.`;
}

function describeTrend(analytics) {
  if (analytics.alert?.type === 'overtraining') {
    return `Your training volume has gone up recently but your strength numbers have dipped a bit — that combo usually means more recovery is needed, not more work.`;
  }
  if (analytics.alert?.type === 'performance_drop') {
    return `Your numbers have dropped over the last couple of weeks — worth checking in on sleep, stress, and recovery.`;
  }
  if (analytics.intensityTrend === 'increasing') {
    return `Strength is trending up nicely across your lifts.`;
  }
  if (analytics.intensityTrend === 'decreasing') {
    return `Working weights have eased off a little recently — nothing alarming, just something to keep an eye on.`;
  }
  return null;
}

function describePR(analytics) {
  const prFreq = parseFloat(analytics.prFrequency || 0);
  if (prFreq > 0 && analytics.daysSinceLastPR != null && analytics.daysSinceLastPR <= 14) {
    const when = analytics.daysSinceLastPR === 0 ? 'today' : `in the last ${analytics.daysSinceLastPR} days`;
    return `You hit a new PR ${when} — keep that momentum going.`;
  }
  return null;
}

function describeStalledOrSkipped(analytics) {
  const stalled = analytics.needsAttention || [];
  if (stalled.length > 0) {
    const names = stalled.slice(0, 2).map(l => l.exerciseName).join(' and ');
    const verb = stalled.length > 1 || names.includes(' and ') ? 'have' : 'has';
    return `${names} ${verb} plateaued a bit — might be worth a rep-range or exercise swap there.`;
  }
  const topSkipped = (analytics.skippedExercises || []).filter(e => e.count >= 2);
  if (topSkipped.length > 0) {
    return `Noticed ${topSkipped[0].name} has been skipped a few times — let me know if something's not working with it.`;
  }
  return null;
}

export function generatePerformanceBriefing(analytics, { timeRangeWeeks = 12 } = {}) {
  if (!analytics) return '';

  const totalSessions = (analytics.sessionFrequency || []).reduce((sum, w) => sum + w.sessions, 0);
  const sentences = [describeAdherence(totalSessions, timeRangeWeeks, analytics.weeklyTarget)];

  if (totalSessions > 0) {
    const trend = describeTrend(analytics);
    if (trend) sentences.push(trend);

    const pr = describePR(analytics);
    if (pr) sentences.push(pr);

    const stalledOrSkipped = describeStalledOrSkipped(analytics);
    if (stalledOrSkipped) sentences.push(stalledOrSkipped);
  }

  return sentences.join(' ');
}

// ── The whole-picture brief ──────────────────────────────────────────────
// One overall note for the coach to edit and send: training (above), then the weight trend, then what
// they log eating (daily calorie and protein totals only), then anything the client keeps swapping or
// has had pain with. Still pure: the weigh-ins, the food totals and the swap groups to act on come
// from the caller.

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const shortDate = (date) => `${date.getDate()} ${MONTHS[date.getMonth()]}`;
const kgText = (n) => `${Number(n.toFixed(1))} kg`;
const mean = (values) => values.reduce((sum, v) => sum + v, 0) / values.length;

// How far a reading must sit from two neighbours that agree with each other before it is called a slip
// (a wrongly typed weight), and how close the neighbours must be. Ordinary day-to-day changes are
// well inside both.
const SLIP_FROM_NEIGHBOURS = 0.04;
const NEIGHBOURS_AGREE = 0.02;
// Fewer days between the first and last reading than this is too short to call a direction.
const MIN_TREND_DAYS = 10;
// A reading older than this is worth asking for a new one.
const STALE_WEIGH_IN_DAYS = 21;

// weighIns: [{ date: Date, weight: number, source?: string }] in any order. Looks at the last `weeks` weeks.
// A body scan (source 'scan') comes from another machine, which reads differently from a scale and from
// other machines, so the direction is read from scale readings only (the weight chart's rule: trends
// compare the same machine). A scan still counts as the latest weigh-in.
export function summariseWeight(weighIns, { weeks = 8, now = new Date() } = {}) {
  const from = now.getTime() - weeks * 7 * DAY_MS;
  const inWindow = (weighIns || [])
    .filter((w) => w && w.date instanceof Date && !Number.isNaN(w.date.getTime())
      && Number(w.weight) > 0 && w.date.getTime() >= from && w.date.getTime() <= now.getTime() + DAY_MS)
    .sort((a, b) => a.date - b.date);

  const scale = inWindow.filter((w) => w.source !== 'scan');
  const scans = inWindow.filter((w) => w.source === 'scan');

  // One reading far from two that agree with each other is a slip: left out, and said so.
  const slips = [];
  const kept = scale.filter((w, i) => {
    const before = scale[i - 1];
    const after = scale[i + 1];
    if (!before || !after) return true;
    const around = (before.weight + after.weight) / 2;
    const neighboursAgree = Math.abs(before.weight - after.weight) <= around * NEIGHBOURS_AGREE;
    if (neighboursAgree && Math.abs(w.weight - around) >= around * SLIP_FROM_NEIGHBOURS) {
      slips.push(w);
      return false;
    }
    return true;
  });

  const lastScale = kept[kept.length - 1] ?? null;
  const latest = [...kept, ...scans].sort((a, b) => a.date - b.date).pop() ?? null;
  const spanDays = kept.length > 1 ? (lastScale.date - kept[0].date) / DAY_MS : 0;
  let trend = null;
  if (kept.length >= 2 && spanDays >= MIN_TREND_DAYS) {
    // Averaging the first and last couple of readings keeps one heavy or light day from deciding it
    const n = Math.max(1, Math.min(2, Math.floor(kept.length / 2)));
    const change = mean(kept.slice(-n).map((w) => w.weight)) - mean(kept.slice(0, n).map((w) => w.weight));
    const steadyWithin = Math.max(0.5, lastScale.weight * 0.005);
    trend = {
      to: lastScale.weight,
      change,
      direction: Math.abs(change) < steadyWithin ? 'steady' : change < 0 ? 'down' : 'up',
      weeks: Math.max(1, Math.round(spanDays / 7)),
    };
  }
  return {
    count: kept.length + scans.length,
    latest,
    trend,
    slips,
    daysSinceLatest: latest ? Math.floor((now.getTime() - latest.date.getTime()) / DAY_MS) : null,
  };
}

// Facts only, and the same wording for every client (Codex's review of plan 013, 27 October 2026): no praise
// or worry about the direction, no request to weigh in. The coach says what they think when they edit it.
export function describeWeight(weighIns, { weeks = 8, now = new Date() } = {}) {
  const w = summariseWeight(weighIns, { weeks, now });
  const sentences = [];
  const weeksOf = (n) => `${n} ${n === 1 ? 'week' : 'weeks'}`;

  if (w.count === 0) {
    sentences.push(`No weigh-in logged in the last ${weeks} weeks.`);
  } else if (!w.trend) {
    sentences.push(`Your last weigh-in was ${kgText(w.latest.weight)} on ${shortDate(w.latest.date)}.`);
  } else if (w.trend.direction === 'steady') {
    sentences.push(`Your weight has held steady around ${kgText(w.trend.to)} over the last ${weeksOf(w.trend.weeks)}.`);
  } else {
    sentences.push(`Your weight is ${w.trend.direction} about ${kgText(Math.abs(w.trend.change))} over the last ${weeksOf(w.trend.weeks)}, to ${kgText(w.trend.to)}.`);
  }

  if (w.count > 0 && w.daysSinceLatest > STALE_WEIGH_IN_DAYS) {
    sentences.push(`Your last weigh-in was ${Math.round(w.daysSinceLatest / 7)} weeks ago.`);
  }
  if (w.slips.length === 1) {
    sentences.push(`One weigh-in (${kgText(w.slips[0].weight)} on ${shortDate(w.slips[0].date)}) looks out of line with the others, so I've left it out — tell me if it's right.`);
  } else if (w.slips.length > 1) {
    sentences.push(`${w.slips.length} weigh-ins look out of line with the others, so I've left them out — tell me if any are right.`);
  }
  return sentences.join(' ');
}

const nameList = (names) => {
  if (names.length <= 3) return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
};

// groups: the swap groups to act on (structureSwaps(...).actOn)
export function describeSwaps(groups) {
  const named = (groups || []).filter((g) => g.originalName);
  if (named.length === 0) return '';
  const pain = named.filter((g) => g.hasDiscomfort).map((g) => g.originalName);
  const often = named.filter((g) => !g.hasDiscomfort).map((g) => g.originalName);
  const sentences = [];
  if (pain.length > 0) {
    sentences.push(`You gave pain or discomfort as the reason for swapping ${nameList(pain)} — tell me how that feels and we'll find something that works for you.`);
  }
  if (often.length > 0) {
    sentences.push(`${nameList(often)} ${often.length === 1 ? 'has' : 'have'} been swapped several times, so I'll look at replacing ${often.length === 1 ? 'it' : 'them'} in your program.`);
  }
  return sentences.join(' ');
}

// A day with less food than this logged looks only partly logged (a snack and nothing else), so it is not
// counted in the average. With a plan target on file the bar is half the target if that is higher.
const FOOD_FULL_DAY_KCAL = 800;
const nice = (n) => Math.round(n).toLocaleString('en-GB');
const localDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// food: what coach_food_summary (migration 100) returns, or null when there is nothing to show: daily
// calorie and protein totals and the plan's numbers, never what was eaten. null (also: not allowed, not
// loaded, or a client whose eating-disorder history is never shown to staff) says nothing about food,
// and in particular never asks that client to log.
export function describeFood(food, { now = new Date() } = {}) {
  if (!food || !Array.isArray(food.days)) return '';
  const windowDays = food.window_days || 14;
  const today = localDay(now);
  // tomorrow's date can appear for a client ahead of the server; it is not one of the days so far
  const days = food.days.filter((d) => d && d.entries > 0 && d.d <= today);
  // Nothing logged, and no totals to show, read the same: nothing is said and nobody is asked to log
  if (days.length === 0) return '';
  const target = Number(food.target_calories) > 0 ? Number(food.target_calories) : null;
  const bar = Math.max(FOOD_FULL_DAY_KCAL, target ? target / 2 : 0);
  const full = days.filter((d) => d.kcal != null && d.kcal >= bar);
  const logged = `You've logged food on ${days.length} of the last ${windowDays} days`;
  if (full.length === 0) {
    return `${logged}, though the days look only partly logged, so I can't give a reliable average yet.`;
  }

  const kcal = Math.round(full.reduce((sum, d) => sum + d.kcal, 0) / full.length / 10) * 10;
  const withProtein = full.filter((d) => d.protein_g != null);
  const protein = withProtein.length > 0 ? Math.round(withProtein.reduce((sum, d) => sum + d.protein_g, 0) / withProtein.length) : null;
  const targetProtein = Number(food.target_protein_g) > 0 ? Number(food.target_protein_g) : null;

  let sentence = `${logged}; on ${full.length === 1 ? 'the day that looks' : `the ${full.length} days that look`} fully logged you averaged about ${nice(kcal)} kcal`;
  if (protein != null) sentence += ` and ${protein} g protein`;
  sentence += '.';

  // Their plan's numbers and the "outgoing" side (what the body uses in a day, estimated from their
  // activity level when their plan was made), side by side with what they logged and no verdict on it
  const burn = Number(food.maintenance_calories) > 0 ? Number(food.maintenance_calories) : null;
  const plan = [];
  if (target) plan.push(`your plan's target is ${nice(target)} kcal${targetProtein ? ` and ${targetProtein} g protein` : ''}`);
  if (burn) plan.push(`your body uses roughly ${nice(burn)} kcal a day at your activity level`);
  if (plan.length > 0) sentence += ` ${plan.join(', and ').replace(/^./, (c) => c.toUpperCase())}.`;
  return sentence;
}

// The food sentence in a brief came from what the client shared when it was written. Just before the note
// is sent, the page asks again; if the client has stopped sharing, the note must not go out carrying it.
//   foodSentence   the sentence describeFood gave for the totals the page holds ('' if the note has none)
//   stillShared    the totals could be read again just now (a failed read counts as "no": fail closed)
//   confirmedText  the exact text the coach has already been warned about and chose to send as it is
// Returns { action: 'send' | 'removed' | 'blocked', text }:
//   send     nothing to hold back (no food in it, still shared, nothing of it left, or confirmed)
//   removed  the sentence was exactly as written: taken out, and the coach reads the note once more
//   blocked  the sentence was edited and its numbers are still there: the coach is told and chooses
const FOOD_TOKENS = /logged food on \d+ of the last \d+ days|\d[\d,]* (?:kcal|g protein)/g;
export function guardFoodBeforeSend({ text, foodSentence, stillShared, confirmedText = null }) {
  if (!foodSentence || stillShared) return { action: 'send', text };
  if (confirmedText !== null && confirmedText === text) return { action: 'send', text };
  if (text.includes(foodSentence)) {
    return { action: 'removed', text: text.replace(foodSentence, '').replace(/ {2,}/g, ' ').trim() };
  }
  const leftOver = (foodSentence.match(FOOD_TOKENS) || []).some((token) => text.includes(token));
  return leftOver ? { action: 'blocked', text } : { action: 'send', text };
}

// weighIns: [{ date, weight }]; food: coach_food_summary (or null); swaps: structureSwaps(...).actOn
export function generateCoachBrief(analytics, { timeRangeWeeks = 12, weighIns = analytics?.weighIns, food = null, swaps = [], now = new Date() } = {}) {
  if (!analytics) return '';
  // A long chart range is too long for "how is your weight going": between a month and three
  const weightWeeks = Math.min(12, Math.max(4, timeRangeWeeks));
  return [
    generatePerformanceBriefing(analytics, { timeRangeWeeks }),
    describeWeight(weighIns, { weeks: weightWeeks, now }),
    describeFood(food, { now }),
    describeSwaps(swaps),
  ].filter(Boolean).join(' ');
}
